import React, { useState, useCallback, useEffect, useRef } from 'react';
import Card from './Card';

/**
 * Main game table view (shown during 'playing' phase).
 *
 * Props:
 *   gameState     — full (sanitized) game state from server
 *   myUserId      — current user's id string
 *   onPlayCard    — fn(cardIds: string[], source: string)
 *   onPickupPile  — fn()
 *   onOpeningPass — fn()
 *   playResult    — last play-result event { success, cards, boweled, penalty }
 */
const GameTable = ({ gameState, myUserId, onPlayCard, onPickupPile, onOpeningPass, playResult }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showThreeFlash, setShowThreeFlash] = useState(false);
  const [battleCryFlash, setBattleCryFlash] = useState(null);
  const lastThreePlayIdRef = useRef(null);
  const boardWrapRef = useRef(null);
  const hasCenteredBoardRef = useRef(false);
  const OPPONENT_SEAT_ORDER = [2, 1, 3, 6, 9, 7, 4];

  // Derived from gameState — computed before hooks so values are stable
  const players = gameState?.players ?? [];
  const me = players.find((p) => p.userId?.toString() === myUserId) ?? null;
  const viewerIsWaitingRoom = !!gameState?.viewerIsWaitingRoom;
  const viewerDisplayName = (() => {
    const queued = (gameState?.queuedPlayers ?? []).find((p) => p.userId?.toString() === myUserId);
    const pending = (gameState?.pendingJoinRequests ?? []).find((p) => p.userId?.toString() === myUserId);
    return queued?.displayName || pending?.displayName || 'Waiting Player';
  })();
  const virtualMe = !me && viewerIsWaitingRoom
    ? {
      userId: myUserId,
      displayName: `${viewerDisplayName} (Waiting)`,
      isBot: false,
      hand: [],
      faceUp: [],
      faceDown: [],
      swapReady: false,
      rematchReady: false,
      activePile: null,
      isFinished: false,
    }
    : null;
  const seatMe = me || virtualMe;
  const isMyTurn = players[gameState?.currentTurnIndex]?.userId?.toString() === myUserId;

  let myActiveSrc = null;
  if (me) {
    if (me.hand?.length > 0) myActiveSrc = 'hand';
    else if (me.faceUp?.length > 0) myActiveSrc = 'faceUp';
    else if (me.faceDown?.length > 0) myActiveSrc = 'faceDown';
  }

  const getCardSource = useCallback((cardId) => {
    if (!me || !cardId) return null;
    if ((me.hand ?? []).some((card) => card.id === cardId)) return 'hand';
    if ((me.faceUp ?? []).some((card) => card.id === cardId)) return 'faceUp';
    if ((me.faceDown ?? []).some((card) => card.id === cardId)) return 'faceDown';
    return null;
  }, [me]);

  const getCardRank = useCallback((cardId) => {
    if (!me || !cardId) return null;
    const allCards = [...(me.hand ?? []), ...(me.faceUp ?? []), ...(me.faceDown ?? [])];
    return allCards.find((card) => card.id === cardId)?.rank ?? null;
  }, [me]);

  const lockedRank = selectedIds.length > 0
    ? getCardRank(selectedIds[0])
    : null;

  const selectedSource = selectedIds.length > 0 ? getCardSource(selectedIds[0]) : null;

  // Keep selection resilient to live state updates (turn advances, pickups, redraws).
  // Without this, stale ids can remain selected and block further clicks until refresh.
  useEffect(() => {
    if (!me) {
      if (selectedIds.length) setSelectedIds([]);
      return;
    }

    const validIds = new Set([
      ...(me.hand ?? []).map((c) => c.id),
      ...(me.faceUp ?? []).map((c) => c.id),
      ...(me.faceDown ?? []).map((c) => c.id),
    ].filter(Boolean));

    setSelectedIds((prev) => {
      if (!prev.length) return prev;
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [me, me?.hand, me?.faceUp, me?.faceDown, selectedIds.length]);

  // All hooks must be called unconditionally before any early return
  const handleCardSelect = useCallback((cardId, cardRank, source) => {
    if (!isMyTurn || myActiveSrc === 'faceDown') return;

    const currentSelectionSource = selectedIds.length > 0 ? getCardSource(selectedIds[0]) : null;
    const currentSelectionRank = selectedIds.length > 0 ? getCardRank(selectedIds[0]) : null;

    if (myActiveSrc === 'hand') {
      if (source === 'faceUp') {
        if (selectedIds.length === 0 || currentSelectionSource !== 'hand') return;
        if (currentSelectionRank !== cardRank) return;
      } else if (source !== 'hand') {
        return;
      }
    } else if (source !== myActiveSrc) {
      return;
    }

    setSelectedIds((prev) => {
      const nextSelection = prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId];

      if (prev.length > 0) {
        const existingRank = getCardRank(prev[0]);
        // If previous selection references a stale card, recover by taking fresh selection.
        if (!existingRank) return [cardId];
        if (cardRank !== existingRank) return prev;
      }

      if (myActiveSrc === 'hand') {
        const nextSources = new Set(nextSelection.map((id) => getCardSource(id)).filter(Boolean));
        if (nextSources.has('faceUp') && !nextSources.has('hand')) return prev;
      }

      return nextSelection;
    });
  }, [getCardRank, getCardSource, isMyTurn, myActiveSrc, selectedIds]);

  const handleSubmitPlay = useCallback(() => {
    if (!selectedIds.length || !myActiveSrc) return;
    const sources = new Set(selectedIds.map((id) => getCardSource(id)).filter(Boolean));
    const playSource = sources.size > 1 ? 'mixed' : sources.values().next().value;
    if (!playSource) return;
    onPlayCard(selectedIds, playSource);
    setSelectedIds([]);
  }, [getCardSource, selectedIds, myActiveSrc, onPlayCard]);

  const handleFaceDownClick = useCallback(() => {
    if (!isMyTurn || myActiveSrc !== 'faceDown') return;
    onPlayCard(['blind'], 'faceDown');
  }, [isMyTurn, myActiveSrc, onPlayCard]);

  const handlePickup = useCallback(() => {
    setSelectedIds([]);
    onPickupPile();
  }, [onPickupPile]);

  const playerBattleCry = playResult?.playerBattleCry || null;
  const centerBattleCry = playResult?.centerBattleCry || null;
  const discardLastSix = (gameState?.recentPlays ?? gameState?.discardPile ?? []).slice(-6);

  useEffect(() => {
    const lastPlay = discardLastSix[discardLastSix.length - 1];
    if (!lastPlay || lastPlay.rank !== '3') return;
    if (lastPlay.id === lastThreePlayIdRef.current) return;

    lastThreePlayIdRef.current = lastPlay.id;
    setShowThreeFlash(true);
    const timer = setTimeout(() => setShowThreeFlash(false), 1300);
    return () => clearTimeout(timer);
  }, [discardLastSix]);

  useEffect(() => {
    const playerBattleCryText = playerBattleCry?.text || '';
    const centerBattleCryText = centerBattleCry?.text || '';
    const nextFlash = playerBattleCryText || centerBattleCryText ? {
      actorUserId: playResult?.actorUserId || null,
      actorDisplayName: playResult?.actorDisplayName || 'Player',
      playerBattleCry,
      centerBattleCry,
    } : null;

    if (!nextFlash) {
      setBattleCryFlash(null);
      return undefined;
    }

    setBattleCryFlash(nextFlash);
    const timer = setTimeout(() => setBattleCryFlash(null), 1800);
    return () => clearTimeout(timer);
  }, [playResult?.actorUserId, playResult?.actorDisplayName, playerBattleCry, centerBattleCry]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const centerMobileBoard = () => {
      if (!window.matchMedia('(max-width: 760px)').matches) return;
      const boardWrap = boardWrapRef.current;
      if (!boardWrap) return;
      boardWrap.scrollLeft = Math.max(0, (boardWrap.scrollWidth - boardWrap.clientWidth) / 2);
      hasCenteredBoardRef.current = true;
    };

    if (!hasCenteredBoardRef.current) {
      const timer = window.setTimeout(centerMobileBoard, 0);
      return () => window.clearTimeout(timer);
    }

    window.addEventListener('resize', centerMobileBoard);
    return () => window.removeEventListener('resize', centerMobileBoard);
  }, [gameState?.players?.length]);

  // Early return after all hooks
  if (!gameState) return <div className="game-table">Loading game...</div>;

  const myIndex = players.findIndex((p) => p.userId?.toString() === myUserId);
  const clockwiseOpponents = myIndex >= 0
    ? Array.from({ length: players.length - 1 }, (_, offset) => {
      const idx = (myIndex + 1 + offset) % players.length;
      return { seatPlayer: players[idx], playerIndex: idx };
    })
    : players.map((seatPlayer, idx) => ({ seatPlayer, playerIndex: idx }));
  const seatAssignments = new Map();
  if (seatMe) {
    seatAssignments.set(8, { seatPlayer: seatMe, playerIndex: myIndex });
  }
  clockwiseOpponents.forEach((entry, idx) => {
    const seat = OPPONENT_SEAT_ORDER[idx];
    if (seat) seatAssignments.set(seat, entry);
  });

  const drawCount = gameState.drawPile?.[0]?.count ?? 0;
  const discardTop = gameState.discardPile?.[gameState.discardPile.length - 1];
  const currentPlayerName = players[gameState.currentTurnIndex]?.displayName ?? '...';
  const canPickup = isMyTurn
    && selectedIds.length === 0
    && ((gameState.discardPile?.length ?? 0) > 0 || !!gameState.threeActive);
  const directionSymbol = gameState.direction === -1 ? '↺' : '↻';
  const directionLabel = gameState.direction === -1 ? 'Counter-clockwise' : 'Clockwise';
  const openingRequirement = gameState?.openingRequirement;
  const openingPromptActive = !!(openingRequirement?.rank && openingRequirement?.color);
  const openingLabel = openingPromptActive
    ? `${openingRequirement.color === 'red' ? 'Red' : 'Black'} ${openingRequirement.rank}`
    : '';
  const myHasOpeningCard = openingPromptActive
    && myActiveSrc === 'hand'
    && (me?.hand ?? []).some((c) => c.rank === openingRequirement.rank && (openingRequirement.color === 'red'
      ? ['hearts', 'diamonds'].includes(c.suit)
      : ['clubs', 'spades'].includes(c.suit)));
  const canOpeningPass = isMyTurn && openingPromptActive && selectedIds.length === 0 && !myHasOpeningCard;

  const valueToRank = {
    2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K', 14: 'A',
  };

  const currentRuleText = (() => {
    if (openingPromptActive) return `Opening check: play ${openingLabel} (clockwise ask)`;
    if (gameState.threeActive) return 'Current rule: only 3 is playable';
    if (gameState.sevenActive) return 'Current rule: must play 7 or lower';
    if (!gameState.activePileValue) return 'Current rule: any card can open';
    return `Current rule: play ${valueToRank[gameState.activePileValue] || gameState.activePileValue} or higher`;
  })();

  const renderMyCards = (cards, source) =>
    (cards ?? []).map((card) => {
      const isSelected = selectedIds.includes(card.id);
      const isPlayable = isMyTurn && (
        myActiveSrc === source
        || (myActiveSrc === 'hand' && source === 'faceUp' && selectedIds.length > 0 && selectedSource === 'hand')
      );
      const isDisabled = isPlayable && lockedRank && card.rank !== lockedRank;
      return (
        <div
          key={card.id}
          className={[
            'card-wrapper',
            isPlayable && !isDisabled ? 'playable' : '',
            isSelected ? 'selected' : '',
            isDisabled ? 'dimmed' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => isPlayable && !isDisabled && handleCardSelect(card.id, card.rank, source)}
          title={isPlayable && !isDisabled ? `Select ${card.rank}` : ''}
        >
          <Card card={card} />
        </div>
      );
    });

  const renderBattleCry = (message, className) => {
    if (!message?.text) return null;
    return (
      <div className={className}>
        <span>{message.text}</span>
      </div>
    );
  };

  const renderTablePiles = ({
    seatPlayer,
    interactive = false,
    compact = false,
    slotKeyPrefix = 'table-pile',
    blockLabel = 'Table piles (face-up over face-down)',
  }) => {
    const canRevealFaceDown = interactive && myActiveSrc === 'faceDown';
    const canPlayFaceUp = interactive && (
      myActiveSrc === 'faceUp'
      || (myActiveSrc === 'hand' && selectedIds.length > 0 && selectedSource === 'hand')
    );

    return (
    <div className={`table-piles-block ${canRevealFaceDown ? 'table-piles-block--playable' : ''} ${compact ? 'table-piles-block--compact' : ''}`}>
      <p className="card-row-label">{blockLabel}</p>
      <div
        className={`table-piles-grid ${compact ? 'table-piles-grid--compact' : ''}`}
        onClick={canRevealFaceDown ? handleFaceDownClick : undefined}
        title={canRevealFaceDown ? 'Click table piles to reveal one face-down card to your hand' : ''}
      >
        {[0, 1, 2].map((slotIdx) => {
          const faceDownCard = seatPlayer.faceDown?.[slotIdx] || null;
          const faceUpCard = seatPlayer.faceUp?.[slotIdx] || null;
          const isSelected = faceUpCard ? selectedIds.includes(faceUpCard.id) : false;
          const isPlayableFaceUp = canPlayFaceUp && !!faceUpCard;
          const isDisabled = isPlayableFaceUp && lockedRank && faceUpCard.rank !== lockedRank;
          return (
            <div key={`${slotKeyPrefix}-${slotIdx}`} className="table-pile-slot">
              <div className="table-pile-base">
                {faceDownCard
                  ? <Card card={{ hidden: true }} size="sm" />
                  : <div className="table-card-placeholder" />}
              </div>
              {faceUpCard && (
                <div
                  className={[
                    'table-pile-overlay',
                    isPlayableFaceUp && !isDisabled ? 'playable' : '',
                    isSelected ? 'selected' : '',
                    isDisabled ? 'dimmed' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={(e) => {
                    if (!interactive) return;
                    e.stopPropagation();
                    if (isPlayableFaceUp && !isDisabled) handleCardSelect(faceUpCard.id, faceUpCard.rank, 'faceUp');
                  }}
                  title={isPlayableFaceUp && !isDisabled ? `Select ${faceUpCard.rank}` : ''}
                >
                  <Card card={faceUpCard} size="sm" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canRevealFaceDown && (
        <p className="blind-hint">Click table piles to reveal one face-down card to hand</p>
      )}
    </div>
  );
  };

  const renderOpponentTile = (seat, seatPlayer, playerIndex) => {
    const opTurn = gameState.currentTurnIndex === playerIndex;
    const actorMatchesTile = battleCryFlash?.actorUserId && seatPlayer.userId?.toString() === battleCryFlash.actorUserId.toString();
    return (
      <div className={`seat-tile seat-tile--opponent ${opTurn ? 'active-turn' : ''}`}>
        <p className="opponent-name">Tile {seat}: {seatPlayer.displayName}{seatPlayer.isBot ? ' 🤖' : ''}{opTurn ? ' 🎯' : ''}</p>
        {actorMatchesTile && renderBattleCry(battleCryFlash.playerBattleCry, 'battle-cry-banner battle-cry-banner--player')}
        <div className="opponent-cards">
          {renderTablePiles({
            seatPlayer,
            interactive: false,
            compact: true,
            slotKeyPrefix: `op-${seat}`,
            blockLabel: `Table piles (${(seatPlayer.faceDown ?? []).length} down / ${(seatPlayer.faceUp ?? []).length} up)`,
          })}
          <div className="card-row">
            <p className="card-row-label">Hand ({(seatPlayer.hand ?? []).length})</p>
            <div className="cards">
              {(seatPlayer.hand ?? []).map((_, i) => <Card key={i} card={{ hidden: true }} size="sm" />)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCenterTile = () => (
    <div className="seat-tile seat-tile--center">
      <div className={`turn-indicator ${isMyTurn ? 'your-turn' : ''}`}>
        {isMyTurn ? '⚡ YOUR TURN' : `Waiting for ${currentPlayerName}...`}
        <span className="direction-arrow" title="Play direction">{directionSymbol}</span>
      </div>
      <div className="direction-status" aria-live="polite">
        <span className="direction-status-icon" aria-hidden="true">{directionSymbol}</span>
        <span className="direction-status-text">Direction: {directionLabel}</span>
      </div>
      <div className="center-area">
        <div className="pile draw-pile">
          <Card card={{ hidden: true }} />
          <p>{drawCount} cards left</p>
        </div>
        <div className="pile discard-pile">
          {discardTop ? <Card card={discardTop} /> : <div className="card card-empty" />}
          <p>Play pile ({gameState.discardPile?.length ?? 0})</p>
        </div>
      </div>
      {renderBattleCry(battleCryFlash?.centerBattleCry, 'battle-cry-banner battle-cry-banner--center')}
      <p className="current-rule-text">{currentRuleText}</p>
      <div className="discard-history">
        <p className="card-row-label">Recent actions (last 6)</p>
        <div className="cards discard-history-cards">
          {discardLastSix.length > 0
            ? discardLastSix.map((c, idx) => {
              const isPickupMarker = c?.markerType === 'pickup';
              const isBowelMarker = c?.markerType === 'bowel';
              const historyKey = `${c.id || `${c.rank}-${c.value || 'x'}-${idx}`}-${idx}`;
              const transformedFromEight = c?.transformedFromRank === '8' && c?.rank !== '8';
              if (isPickupMarker) {
                return (
                  <div
                    key={historyKey}
                    className="discard-history-marker-shell"
                    title={c?.markerLabel || 'Pickup pile'}
                  >
                    <span className="discard-history-marker-bar" aria-hidden="true" />
                    <span className="discard-history-marker-dot" aria-hidden="true" />
                    <span className="discard-history-marker-label">Pickup</span>
                  </div>
                );
              }
              if (isBowelMarker) {
                return (
                  <div
                    key={historyKey}
                    className="discard-history-marker-shell discard-history-marker-shell--bowel"
                    title={c?.markerLabel || 'Bowel pile'}
                  >
                    <span className="discard-history-marker-x" aria-hidden="true">X</span>
                    <span className="discard-history-marker-label">Bowel</span>
                  </div>
                );
              }
              return (
                <div
                  key={historyKey}
                  className="discard-history-card-shell"
                  title={transformedFromEight ? `Transparent 8 became ${c.rank}` : undefined}
                >
                  <Card card={c} size="sm" />
                  {transformedFromEight && <span className="transparent-eight-badge">8</span>}
                </div>
              );
            })
            : <p className="discard-history-empty">No cards in play pile yet</p>}
        </div>
      </div>
      {showThreeFlash && (
        <div className="three-flash-overlay" aria-hidden="true">
          <div className="three-flash-card">3</div>
          <p className="three-flash-text">THREED!</p>
        </div>
      )}
      <p className="center-seat-label">Tile 5 (center)</p>
    </div>
  );

  const renderMyTile = (seatPlayer, playerIndex) => (
    <div className={`seat-tile seat-tile--me ${gameState.currentTurnIndex === playerIndex ? 'active-turn' : ''}`}>
      <p className="my-name">Tile 8: {seatPlayer.displayName} (You)</p>
      {battleCryFlash?.actorUserId && battleCryFlash.actorUserId.toString() === myUserId?.toString() && renderBattleCry(battleCryFlash.playerBattleCry, 'battle-cry-banner battle-cry-banner--player')}

      <div className="table-piles-row">
        {renderTablePiles({
          seatPlayer,
          interactive: isMyTurn,
          compact: false,
          slotKeyPrefix: 'me',
        })}

        {isMyTurn && (
          <div className="table-piles-actions">
            <div className="action-bar action-bar--inline">
              {selectedIds.length > 0 ? (
                <>
                  <button className="btn-play" onClick={handleSubmitPlay}>
                    Play {selectedIds.length} card{selectedIds.length > 1 ? 's' : ''}
                    {selectedIds.length >= 4 ? ' 💀 BOWEL!' : ''}
                  </button>
                  <button className="btn-cancel" onClick={() => setSelectedIds([])}>Cancel</button>
                </>
              ) : (
                <>
                  {canPickup && (
                    <button className="btn-pickup" onClick={handlePickup}>
                      Pick Up Pile
                    </button>
                  )}
                  {canOpeningPass && (
                    <button className="btn-cancel" onClick={onOpeningPass}>
                      Pass (No {openingLabel})
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card-row">
        <p className="card-row-label">Hand</p>
        <div className="cards">{renderMyCards(seatPlayer.hand, 'hand')}</div>
      </div>

      {lockedRank && isMyTurn && (
        <p className="selection-hint">
          Selecting {lockedRank}s — click more {lockedRank}s to play together, then hit Play
        </p>
      )}
    </div>
  );

  const renderSeatTile = (seat) => {
    if (seat === 5) return renderCenterTile();
    const assignment = seatAssignments.get(seat);
    if (!assignment) {
      return (
        <div className="seat-tile seat-tile--empty">
          <p className="empty-seat-label">Tile {seat}</p>
          <p className="empty-seat-subtitle">Empty seat</p>
        </div>
      );
    }

    if (seat === 8) {
      return renderMyTile(assignment.seatPlayer, assignment.playerIndex);
    }

    return renderOpponentTile(seat, assignment.seatPlayer, assignment.playerIndex);
  };

  return (
    <div className="game-table">
      {/* Game flag banners */}
      {gameState.sevenActive && (
        <div className="game-flag-banner banner--seven">
          ⚠️ 7 Rule active — you must play a 7 or lower!
        </div>
      )}
      {gameState.threeActive && (
        <div className="game-flag-banner banner--three">
          ⚠️ 3 Rule active — only a 3 can be played!
        </div>
      )}
      {playResult && !playResult.success && playResult.penalty && (
        <div className="game-flag-banner banner--penalty">
          ✗ Blind flip failed — pile added to hand!
        </div>
      )}
      {playResult?.flippedToHand && (
        <div className="game-flag-banner banner--penalty">
          👁️ Face-down card revealed to your hand. Play it if legal, or pick up pile.
        </div>
      )}
      {openingPromptActive && (
        <div className="game-flag-banner banner--opening">
          🎴 Opening round: {currentPlayerName}, do you have {openingLabel}?
        </div>
      )}

      <div className="board-wrap" ref={boardWrapRef}>
        <div className="board-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((seat) => (
            <div key={seat} className={`board-cell board-cell--${seat}`}>
              {renderSeatTile(seat)}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default GameTable;
